import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Page, PlayerCard, PlayerCardFilters, PlayerCardServices, PlayerCardSortColumn } from "../services/bedrock";
import { useSocialCoachSession } from "./useSocialCoachSession";

const PLAYER_CARDS_LIST_QUERY_KEY = "coachPlayerCardsList";
const PLAYER_CARDS_LIST_STALE_TIME = 1000 * 60 * 5; // 5 minutes

export interface GetPlayerCardsParams {
  page?: number;
  perPage?: number;
  filters?: PlayerCardFilters;
  sort?: PlayerCardSortColumn;
  order?: "asc" | "desc";
}

function filterKey(value?: number | string | number[]) {
  if (Array.isArray(value)) return [...value].map(Number).sort((a, b) => a - b);
  return value ?? null;
}

function buildPlayerCardsListQueryOptions(coachId: number | undefined, params: GetPlayerCardsParams = {}) {
  const page = params.page ?? 1;
  const perPage = params.perPage ?? 15;
  return {
    queryKey: [
      PLAYER_CARDS_LIST_QUERY_KEY,
      coachId,
      page,
      perPage,
      filterKey(params.filters?.playerId),
      filterKey(params.filters?.themeId),
      filterKey(params.filters?.templateId),
      filterKey(params.filters?.branchId),
      filterKey(params.filters?.regionId),
      params.sort ?? null,
      params.order ?? null,
    ] as const,
    queryFn: () => PlayerCardServices.getPlayerCards(page, perPage, params.filters, params.sort, params.order),
    staleTime: PLAYER_CARDS_LIST_STALE_TIME,
  };
}

export const usePlayerCardFetch = () => {
  const { coachId } = useSocialCoachSession();
  const queryClient = useQueryClient();

  const fetchPlayerCards = useCallback(
    (params: GetPlayerCardsParams = {}) => queryClient.fetchQuery(buildPlayerCardsListQueryOptions(coachId, params)),
    [coachId, queryClient]
  );

  const getCachedPlayerCards = useCallback(
    (params: GetPlayerCardsParams = {}) => {
      const queryKey = buildPlayerCardsListQueryOptions(coachId, params).queryKey;
      const state = queryClient.getQueryState<Page<PlayerCard>>(queryKey);
      if (!state?.data || state.isInvalidated) return undefined;
      if (Date.now() - state.dataUpdatedAt > PLAYER_CARDS_LIST_STALE_TIME) return undefined;
      return state.data;
    },
    [coachId, queryClient]
  );

  const clearPlayerCardsCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [PLAYER_CARDS_LIST_QUERY_KEY, coachId] }).catch(console.error);
  }, [coachId, queryClient]);

  return useMemo(
    () => ({
      fetchPlayerCards,
      getCachedPlayerCards,
      clearPlayerCardsCache,
    }),
    [fetchPlayerCards, getCachedPlayerCards, clearPlayerCardsCache]
  );
};
